import { localUrl as appLocalUrl } from '../app-path.js';
import ApiClient from '../api-client';
import SessionManager from '../session-manager';
import SubscriptionSyncService from './subscription-sync-service';
import FeatureManager from '../feature-manager';
import { trackGa4CatalogEvent } from '../analytics/ga4-event-tracker';
import FavoriteCityService from '../components/favorite-city-service';

const AuthService = {
	sanitizeRedirectPath(redirectPath, fallback = '') {
		const value = String(redirectPath || '').trim();

		if (!value) {
			return fallback;
		}

		if (!value.startsWith('/') || value.startsWith('//')) {
			return fallback;
		}

		if (value.includes('://') || value.includes('\\')) {
			return fallback;
		}

		return value;
	},

	async login(email, password, options = {}) {
		try {
			ApiClient.init();
			const safeRedirect = this.sanitizeRedirectPath(options.redirect);

			const response = await ApiClient.axiosInstance.post(
				appLocalUrl('/v2/json/auth/login'),
				{ email, password, redirect: safeRedirect || undefined },
			);

			const userData = response.data;

			SessionManager.setSession({
				accessToken: userData.accessToken,
				refreshToken: userData.refreshToken,
				name: userData.name,
				userId: userData.userId,
				orderId: userData.orderId,
				email: userData.email || email,
				image: userData.image,
			});
			SubscriptionSyncService.startAutoSync(ApiClient);
			await SubscriptionSyncService.syncPlan(ApiClient, true);
			await FavoriteCityService.fulfillPendingFavorite('auth');

			const completeUserData = SessionManager.getUserData();
			trackGa4CatalogEvent('login', {
				user_id: completeUserData.userId,
				method: 'email',
			});

			return {
				success: true,
				redirect: userData.redirect || appLocalUrl('/splash/login'),
				user: {
					userId: completeUserData.userId,
					orderId: completeUserData.orderId,
					name: completeUserData.name,
					email: completeUserData.email || email,
					image: completeUserData.image,
				},
			};
		} catch (error) {
			console.error('Erro no login:', error);

			const errorMessage =
				error.response?.data?.error ||
				error.response?.data?.message ||
				'Erro ao fazer login. Tente novamente.';

			return {
				success: false,
				error: errorMessage,
			};
		}
	},

	async logout() {
		try {
			await ApiClient.post(appLocalUrl('/json/myclimatempo/user/logout'));
		} catch (error) {
			console.error('Erro ao fazer logout no servidor:', error);
		} finally {
			const currentUser = SessionManager.getUserData();
			trackGa4CatalogEvent('logout', {
				user_id: currentUser?.userId,
			});
			SubscriptionSyncService.stopAutoSync();
			SubscriptionSyncService.clearSyncTimestamp();
			FeatureManager.clearCache();
			SessionManager.clearSession();
			window.location.href = appLocalUrl('/login');
		}
	},

	async loginGoogle(token, options = {}) {
		try {
			ApiClient.init();
			const safeRedirect = this.sanitizeRedirectPath(options.redirect);

			const response = await ApiClient.axiosInstance.post(
				appLocalUrl('/json/myclimatempo/user/auth/social/login'),
				{
					provider: 'google',
					token: token,
					redirect: safeRedirect || undefined,
				},
				{
					timeout: 30000,
				},
			);

			const data = response.data;

			if (data.success) {
				if (data.accessToken) {
					SessionManager.setSession({
						accessToken: data.accessToken,
						refreshToken: data.refreshToken,
						name: data.user?.name || data.name,
						userId: data.userId,
						orderId: data.orderId,
						email: data.user?.email || data.email,
						image: data.user?.image || data.image,
					});
					SubscriptionSyncService.startAutoSync(ApiClient);
					await SubscriptionSyncService.syncPlan(ApiClient, true);
					await FavoriteCityService.fulfillPendingFavorite('auth');
					const userData = SessionManager.getUserData();
					trackGa4CatalogEvent('login', {
						user_id: userData?.userId,
						method: 'google',
					});
				}

				return {
					success: true,
					redirect: data.redirect || appLocalUrl('/splash/login'),
					user: data.user,
				};
			}

			return {
				success: false,
				error: data.error || 'Erro ao fazer login com Google',
			};
		} catch (error) {
			console.error('Erro no login Google:', error);

			let errorMessage = 'Erro ao conectar ao servidor';

			if (error.response) {
				if (error.response.status === 401) {
					errorMessage = 'Token do Google inválido. Tente novamente.';
				} else if (error.response.status === 503) {
					errorMessage =
						'Servidor temporariamente indisponível. Tente novamente.';
				} else {
					errorMessage =
						error.response.data?.error ||
						error.response.data?.message ||
						'Erro ao fazer login com Google';
				}
			} else if (error.code === 'ECONNABORTED') {
				errorMessage = 'Tempo de conexão esgotado. Tente novamente.';
			}

			return {
				success: false,
				error: errorMessage,
			};
		}
	},

	async loginApple(
		identityToken,
		authorizationCode = null,
		user = null,
		options = {},
	) {
		try {
			ApiClient.init();
			const safeRedirect = this.sanitizeRedirectPath(options.redirect);

			const response = await ApiClient.axiosInstance.post(
				appLocalUrl('/json/myclimatempo/user/auth/social/login'),
				{
					provider: 'apple',
					token: identityToken,
					redirect: safeRedirect || undefined,
				},
				{
					timeout: 30000,
				},
			);

			const data = response.data;

			if (data.success) {
				if (data.accessToken) {
					SessionManager.setSession({
						accessToken: data.accessToken,
						refreshToken: data.refreshToken,
						name: data.user?.name || data.name,
						userId: data.userId,
						orderId: data.orderId,
						email: data.user?.email || data.email,
						image: data.user?.image || data.image,
					});
					SubscriptionSyncService.startAutoSync(ApiClient);
					await SubscriptionSyncService.syncPlan(ApiClient, true);
					await FavoriteCityService.fulfillPendingFavorite('auth');
					const userData = SessionManager.getUserData();
					trackGa4CatalogEvent('login', {
						user_id: userData?.userId,
						method: 'apple',
					});
				}

				return {
					success: true,
					redirect: data.redirect || appLocalUrl('/splash/login'),
					user: data.user,
				};
			}

			return {
				success: false,
				error: data.error || 'Erro ao fazer login com Apple',
			};
		} catch (error) {
			console.error('Erro no login Apple:', error);

			let errorMessage = 'Erro ao fazer login com Apple';

			if (error.response) {
				if (error.response.status === 401) {
					errorMessage = 'Token da Apple inválido. Tente novamente.';
				} else if (error.response.status === 503) {
					errorMessage =
						'Servidor temporariamente indisponível. Tente novamente.';
				} else {
					errorMessage =
						error.response.data?.error ||
						error.response.data?.message ||
						errorMessage;
				}
			} else if (error.code === 'ECONNABORTED') {
				errorMessage = 'Tempo de conexão esgotado. Tente novamente.';
			}

			return {
				success: false,
				error: errorMessage,
			};
		}
	},

	getCurrentUser() {
		return SessionManager.getUserData();
	},

	isAuthenticated() {
		return SessionManager.isAuthenticated();
	},

	handleError(error) {
		if (!error.response) {
			return 'Não foi possível conectar ao servidor. Verifique sua conexão.';
		}

		const { status, data } = error.response;
		const serverMessage = data?.message || data?.error;

		switch (status) {
			case 400:
				return (
					serverMessage || 'Requisição inválida. Verifique os dados enviados.'
				);
			case 401:
				return (
					serverMessage ||
					'Email ou senha incorretos. Verifique suas credenciais.'
				);
			case 403:
				return (
					serverMessage || 'Você não tem permissão para acessar este recurso.'
				);
			case 404:
				return serverMessage || 'Recurso não encontrado.';
			case 422:
				return (
					serverMessage || 'Dados inválidos. Verifique os campos enviados.'
				);
			case 429:
				return 'Muitas tentativas. Aguarde um momento e tente novamente.';
			case 500:
				return 'Erro interno do servidor. Tente novamente mais tarde.';
			case 502:
				return 'Servidor temporariamente indisponível. Tente novamente.';
			case 503:
				return 'Serviço indisponível. Tente novamente mais tarde.';
			default:
				return serverMessage || 'Erro desconhecido. Tente novamente.';
		}
	},
};

export default AuthService;
